import Container from '@mui/material/Container';
import axios from 'axios';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useMatch } from 'react-router-dom';
import SignupRoutes from './SignupRoutes';
import useUpdateEffect from 'react/shared/hooks/UseUpdateEffect';
import apiMapper from 'react/signups/card/ApiMapper';
import api from 'react/signups/card/api';
import CardLoading from 'react/signups/card/common/CardLoading';
import MarketoHiddenForm from 'react/signups/card/common/MarketoHiddenForm';
import ModuleLayout from 'react/signups/card/common/ModuleLayout';
import { MODULES, PSEUDO_MODULES } from 'react/signups/card/recipes/constants';
import { findCookbook } from 'react/signups/card/routing/navigator';

export default function Application({ showOnboardingBeacon }) {
  // Application-level navigation state
  const [loading, setLoading] = useState(true);
  const [currentModule, setCurrentModule] = useState();

  // Application-level persisted state
  const [cardAccountSignup, setCardAccountSignup] = useState({});
  const [cardProgramQuestions, setCardProgramQuestions] = useState({});
  const [user, setUser] = useState({});
  const [organization, setOrganization] = useState({});
  const [account, setAccount] = useState({});
  const [cardAccountTrust, setCardAccountTrust] = useState({});
  const [mdWidthCard, setMdWidthCard] = useState(false);

  // -----------------------------------------------------------------------------------------------

  // Initial hydration of signup and associations --------------------------------------------------

  useEffect(() => {
    const params = {
      card_account_signup: {
        userType: '',
      },
    };
    api.createSignup(params).then((response) => {
      const cardAccountSignupFromResponse = response.data.data;
      const { attributes, id, relationships } = cardAccountSignupFromResponse;
      setCardAccountSignup({ id, relationships, ...attributes });
    });
  }, []);

  useUpdateEffect(() => {
    // Only rehydrate on load
    if (!cardAccountSignup.id || account.id || user.id || cardAccountTrust.id) {
      setLoading(false);
      return;
    }

    const userId = cardAccountSignup.relationships.user?.data?.id;
    const accountId = cardAccountSignup.relationships.account?.data?.id;
    const cardAccountTrustId = cardAccountSignup.relationships.card_account_trust?.data?.id;

    // Return unless we have other relationships to load
    if (!userId && !accountId) {
      setLoading(false);
      return;
    }

    const getUser = api.getUser(userId);
    const getAccount = api.getAccount(accountId);
    const getCardAccountTrust = api.getCardAccountTrust(cardAccountTrustId);

    axios.all([getUser, getAccount, getCardAccountTrust]).then(
      axios.spread((userResponse, accountResponse, cardAccountTrustResponse) => {
        const organization = userResponse.data.organization.data;
        const userData = userResponse.data.user.data;

        setOrganization({
          id: organization.id,
          ...organization.attributes,
          address: {
            id: organization.relationships.address?.data?.id,
            ...organization.relationships.address?.data?.attributes,
          },
        });
        setUser({ id: userData.id, ...userData.attributes });

        if (accountResponse) {
          setAccount(apiMapper.mapServerAccountToClientAccount(accountResponse.data.data));
        }

        if (cardAccountTrustResponse) {
          setCardAccountTrust(
            apiMapper.mapServerCardAccountTrustToClientCardAccountTrust(
              cardAccountTrustResponse.data.data,
            ),
          );
        }

        // TODO: Set page?
        setLoading(false);
      }),
    );
  }, [cardAccountSignup]);

  const cookbook = useMemo(() => findCookbook(), []);

  // Placeholder until 'current module' can be resolved from cardAccountSignup
  // Identify from current path, for now
  const credentialsPath = useMatch(cookbook.recipes[MODULES.CREDENTIALS].modulePath);
  const adminPath = useMatch(cookbook.recipes[MODULES.ADMIN].modulePath);
  const idvPath = useMatch(cookbook.recipes[MODULES.IDV].modulePath);
  const cardOrderPath = useMatch(cookbook.recipes[MODULES.CARD_ORDER].modulePath);
  const shippingPath = useMatch(cookbook.recipes[MODULES.SHIPPING].modulePath);
  const reviewPath = useMatch(cookbook.recipes[MODULES.REVIEW].modulePath);
  const trustPath = useMatch(cookbook.recipes[MODULES.TRUST].modulePath);
  // Hack to make Cardholder Profile step have its own display name in Family flow
  const cardholderProfilePath = useMatch('/card_order/cardholder_profile');
  useEffect(() => {
    if (cardholderProfilePath) {
      setCurrentModule(PSEUDO_MODULES.CARDHOLDER_PROFILE);
    } else if (credentialsPath) {
      setCurrentModule(MODULES.CREDENTIALS);
    } else if (adminPath) {
      setCurrentModule(MODULES.ADMIN);
    } else if (idvPath) {
      setCurrentModule(MODULES.IDV);
    } else if (cardOrderPath) {
      setCurrentModule(MODULES.CARD_ORDER);
    } else if (reviewPath) {
      setCurrentModule(MODULES.REVIEW);
    } else if (shippingPath) {
      setCurrentModule(MODULES.SHIPPING);
    } else if (trustPath) {
      setCurrentModule(MODULES.TRUST);
    } else {
      setCurrentModule(MODULES.SURVEY);
    }
  }, [
    cardholderProfilePath,
    credentialsPath,
    adminPath,
    idvPath,
    cardOrderPath,
    reviewPath,
    shippingPath,
    trustPath,
  ]);

  const mdWidthPath = useMatch('/survey/thanks');
  const mdWidth = mdWidthPath && mdWidthCard;

  return (
    <ModuleLayout
      cardholderFirstName={cardAccountSignup.cardholderFirstName}
      currentModule={currentModule}
      userType={cardAccountSignup.userType}
    >
      <main>
        <Container maxWidth={mdWidth ? 'lg' : 'cd'}>
          <MarketoHiddenForm />
          {loading ? (
            <CardLoading />
          ) : (
            <SignupRoutes
              account={account}
              cardAccountSignup={cardAccountSignup}
              cardAccountTrust={cardAccountTrust}
              cardProgramQuestions={cardProgramQuestions}
              cookbook={cookbook}
              currentModule={currentModule}
              organization={organization}
              setAccount={setAccount}
              setCardAccountSignup={setCardAccountSignup}
              setCardAccountTrust={setCardAccountTrust}
              setCardProgramQuestions={setCardProgramQuestions}
              setMdWidthCard={setMdWidthCard}
              setOrganization={setOrganization}
              setUser={setUser}
              showOnboardingBeacon={showOnboardingBeacon}
              user={user}
            />
          )}
        </Container>
      </main>
    </ModuleLayout>
  );
}

Application.propTypes = {
  showOnboardingBeacon: PropTypes.bool,
};
